import {KpiProcessStage, KpiWithValue} from '@hconnect/common/types'
import {dataTestId, Loader} from '@hconnect/uikit'
import {DataTable, customThemeConstants} from '@hconnect/uikit/src/lib2'
import {Add, DeleteOutlined} from '@mui/icons-material'
import {Typography, Box, TextField, Stack, Button, IconButton} from '@mui/material'
import {AxiosError} from 'axios'
import React, {FC, startTransition, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {useSearchParams} from 'react-router'

import {useConfirmDialog} from '../../../../common/providers'
import {useUrlParam} from '../../../../routing'
import {KPI_PARAM_NAME} from '../consts'
import {useDeleteKpi} from '../hooks'

import {KpiLastResult} from './kpi/KpiLastResult'

export const PrePopulatedKpiContainer: FC<{
  data: KpiProcessStage[]
  isLoading: boolean
  error: AxiosError
  searchTerm: string
  onSearchChange: (searchTerm: string) => void
  enableCreationMode: () => void
  selectedProcessStage?: number
}> = ({
  data,
  isLoading,
  error,
  searchTerm,
  onSearchChange,
  enableCreationMode,
  selectedProcessStage = 0
}) => {
  const {t} = useTranslation()

  return (
    <Stack flex={1}>
      <Button
        variant="text"
        startIcon={<Add />}
        sx={{alignSelf: 'flex-start', mb: 1.5}}
        onClick={enableCreationMode}
        {...dataTestId('add_kpi')}
      >
        {t('kpiCalculationSettings.action.addKpi')}
      </Button>
      <TextField
        variant="filled"
        size="small"
        label={t('kpiCalculationSettings.label.search')}
        sx={{mb: 1.5, display: 'block'}}
        onChange={(e) => {
          onSearchChange(e.target.value)
        }}
        value={searchTerm}
        {...dataTestId('kpi-search-input')}
      />
      {isLoading && <Loader />}
      {error?.response?.status === 404 && (
        <Typography px={2} mb={2} color="grey.700">
          {t('kpiCalculationSettings.label.noData')}
        </Typography>
      )}
      {data.map((processStage: KpiProcessStage, index: number) => {
        return (
          <Box hidden={selectedProcessStage !== index} key={processStage.code} width="100%">
            <KpiList kpis={processStage.definitionsWithResults} />
          </Box>
        )
      })}
    </Stack>
  )
}

export const KpiList: FC<{kpis: KpiWithValue[]}> = ({kpis}) => {
  const {
    t,
    i18n: {language}
  } = useTranslation()

  const [, setSearchParam] = useSearchParams()
  const plantCode = useUrlParam('plantCode')
  const {mutate: deleteKpi} = useDeleteKpi()
  const {openDialog} = useConfirmDialog()
  const handleDeleteClick = (e, kpiDefinitionId: number, plantCode: string, kpiName: string) => {
    e.stopPropagation()

    openDialog({
      title: t('kpiCalculationSettings.label.kpiDeleteConfirmationTitle'),
      description: t('kpiCalculationSettings.label.kpiDeleteConfirmation', {kpiName}),
      mainAction: {
        text: t('common.delete'),
        color: 'error',
        icon: <DeleteOutlined />,
        onAction: () => deleteKpi({plantCode, kpiDefinitionId})
      },
      testId: 'delete_kpi_dialog'
    })
  }

  const columns = useMemo(() => {
    return [
      {
        key: 'tagname',
        label: kpis.length
          ? t('kpiCalculationSettings.label.kpis', {count: kpis.length})
          : t('kpiCalculationSettings.label.kpi'),
        customTemplate: (item: KpiWithValue) => (
          <Typography variant="body1">{item.definition.tagname}</Typography>
        )
      },
      {
        key: 'value',
        label: t('kpiCalculationSettings.label.value'),
        customTemplate: (item: KpiWithValue) => {
          return <KpiLastResult language={language} lastResult={item?.lastResult} />
        }
      },
      {
        key: 'unit',
        label: t('kpiCalculationSettings.label.unit'),
        customTemplate: (item: KpiWithValue) => (
          <Typography variant="body1">{item.definition.unit}</Typography>
        )
      },
      {
        key: 'delete',
        customTemplate: (item: KpiWithValue) => (
          <IconButton
            color="error"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
              handleDeleteClick(
                e,
                item.definition.kpiDefinitionId,
                plantCode,
                item.definition.displayName
              )
            }
            {...dataTestId('delete-kpi-button')}
          >
            <DeleteOutlined />
          </IconButton>
        )
      }
    ]
  }, [kpis.length, t, language, handleDeleteClick, plantCode])

  return (
    <DataTable<KpiWithValue>
      columns={columns}
      data={kpis ?? []}
      {...dataTestId('kpi-list-table')}
      onRowClick={(_, kpi) =>
        startTransition(() => setSearchParam({[KPI_PARAM_NAME]: kpi.definition.tagname}))
      }
      keyExtractor={(kpi) => `${kpi.definition.kpiDefinitionId}`}
      rowSx={() => ({
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: customThemeConstants().palette.contentHover
        }
      })}
    />
  )
}
